import React, { useEffect, useState } from 'react';
import Sidebar from './SideBar';
import Coupons from './Coupon/Coupon';
import Customers from './AdminCustomers/Customers';
import PhysicalBookOrder from './PhysicalBookOrder/PhysicalBookOrder';
import OrderRecovery from './OrderRecovery/OrderRecovery';
import GlobalOrders from './Orders/GlobalOrders';
import PrintBatches from './PrintBatches/PrintBatches';
import Settings from './Settings/Settings';
import ErrorLogs from './ErrorLogs/ErrorLogs';
import MidJourneyStats from './MidJourneyStats/MidJourneyStats';
import BookGenerations from './BookGenerations/BookGenerations';
import Users from './Users/Users';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminStats from './AdminStats/AdminStats';
import Plans from './Plans/Plans';
import ArtStyles from './ArtStyles/ArtStyles';
import Heading from '../../components/Common/Heading';
import TrainingVideos from './TrainingVideos/TrainingVideos';
import Students from './Students/Students';
import ProjectTemplates from './ProjectTemplates/ProjectTemplates';
import StoryConfig from './StoryConfig/StoryConfig';
import Districts from './Districts/Districts';
import Schools from './Schools/Schools';
import Countries from './Countries/Countries';
import Classrooms from './Classrooms/Classrooms';
import { UserModel } from '../../interfaces/user.interface';
import InviteUser from './InviteUser/InviteUser';
import MarketingAudit from './MarketingAudit/MarketingAudit';
import { TokenCookieManager } from '../../utils/tokenCookies';
import { AdminNavParams, adminPageLabel } from './adminNav';

const CROSS_ENTITY_PAGES = new Set(['Customers', 'Classrooms', 'Students']);

const Dashboard = () => {
  const [selectedOption, setSelectedOption] = useState('Dashboard');
  const [selectedUserEmail, setSelectedUserEmail] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserModel | null>(null);
  const [initialSearchText, setInitialSearchText] = useState<string | undefined>(undefined);
  const [initialClassroomId, setInitialClassroomId] = useState<string | undefined>(undefined);
  const [initialOrdersChip, setInitialOrdersChip] = useState<string | undefined>(undefined);
  const [initialOrdersStatus, setInitialOrdersStatus] = useState<string | undefined>(undefined);
  const [cameFrom, setCameFrom] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!TokenCookieManager.hasToken()) {
      navigate('/admin');
    }
  });
  const handleOptionClick = (option: string, params?: AdminNavParams) => {
    const navigatingFrom = selectedOption;
    setSelectedOption(option);
    setSelectedUserEmail(null);
    setInitialSearchText(undefined);
    setInitialClassroomId(undefined);
    setInitialOrdersChip(option === 'All Orders' ? params?.ordersChip : undefined);
    setInitialOrdersStatus(option === 'All Orders' ? params?.ordersStatus : undefined);

    // Clear drill-down search params owned by sub-views (Classrooms' ?classroomId
    // / ?projectId) so navigating between sections doesn't re-open a stale
    // drill-down on return. We also reset them when the same section is opened
    // with new params via cross-entity nav.
    if (
      navigatingFrom !== option &&
      (searchParams.get('projectId') || searchParams.get('classroomId'))
    ) {
      const next = new URLSearchParams(searchParams);
      next.delete('projectId');
      next.delete('classroomId');
      setSearchParams(next, { replace: true });
    }
    // Render a "Back to X" breadcrumb whenever a cross-entity link drives the nav
    // (i.e. params provided), but never on plain sidebar clicks.
    const hasParams = !!params && Object.keys(params).some((k) => !!(params as any)[k]);
    if (
      hasParams &&
      CROSS_ENTITY_PAGES.has(navigatingFrom) &&
      CROSS_ENTITY_PAGES.has(option) &&
      navigatingFrom !== option
    ) {
      setCameFrom(navigatingFrom);
    } else {
      setCameFrom(undefined);
    }
    // Translate typed params into the per-page filter props the consumers already use.
    if (params) {
      if (option === 'Customers') {
        setInitialSearchText(params.customerEmail || params.teacherEmail);
      } else if (option === 'Classrooms') {
        setInitialClassroomId(params.classroomId);
        setInitialSearchText(params.teacherEmail);
        // Push the drill-down id into the URL so ClassroomDetailsView opens.
        if (params.classroomId) {
          const next = new URLSearchParams(searchParams);
          next.set('classroomId', params.classroomId);
          next.delete('projectId');
          setSearchParams(next, { replace: true });
        }
      } else if (option === 'Students') {
        setInitialClassroomId(params.classroomId);
      }
    }
  };
  const handleSelectUser = (user: UserModel | undefined) => {
    if (!user?.email) return;
    setSelectedUserEmail(user?.email);
    setSelectedUser(user);
  };


  let content = null;
  let actions = null;
  switch (selectedOption) {
    default:
    case 'Dashboard':
      content = <AdminStats onOptionClick={handleOptionClick} />;
      actions = null;
      break;
    case 'Customers':
      content = <Customers onOptionClick={handleOptionClick} onSelectUser={handleSelectUser} initialSearchText={initialSearchText} />;
      actions = <InviteUser />;
      break;
    case 'Orders':
      content = (
        <PhysicalBookOrder
          user={selectedUser}
          selectedUserEmail={selectedUserEmail!}
          setSelectedUserEmail={setSelectedUserEmail}
          onOptionClick={handleOptionClick}
        />
      );
      actions = null;
      break;
    case 'All Orders':
      content = (
        <GlobalOrders
          key={`${initialOrdersChip || 'all'}|${initialOrdersStatus || ''}`}
          initialChip={(initialOrdersChip as any) || ''}
          initialStatus={initialOrdersStatus}
        />
      );
      actions = null;
      break;
    case 'Print Batches':
      content = <PrintBatches />;
      actions = null;
      break;
    case 'Order Recovery':
      content = <OrderRecovery onOptionClick={handleOptionClick} />;
      actions = null;
      break;
    case 'Coupons':
      content = <Coupons />;
      actions = null;
      break;
    case 'Settings':
      content = <Settings />;
      actions = null;
      break;
    case 'Error Logs':
      content = <ErrorLogs onOptionClick={handleOptionClick} />;
      actions = null;
      break;
    case 'Admin Users':
      content = <Users />;
      actions = null;
      break;
    case 'Plans':
      content = <Plans />;
      actions = null;
      break;
    case 'Art Styles':
      content = <ArtStyles />;
      actions = null;
      break;
    case 'Marketing Audit':
      content = <MarketingAudit onOptionClick={handleOptionClick} initialEmail={initialSearchText} />;
      actions = null;
      break;
    case 'Mid Journey Stats':
      content = <MidJourneyStats />;
      actions = null;
      break;
    case 'Book Generations':
      content = <BookGenerations />;
      actions = null;
      break;

    case 'Students':
      content = <Students onOptionClick={handleOptionClick} initialClassroomId={initialClassroomId} />;
      actions = null;
      break;

    case 'Training Videos':
      content = < TrainingVideos />;
      actions = null;
      break;

    case 'Projects':
      content = <ProjectTemplates />;
      actions = null;
      break;

    case 'Reading Levels':
      content = <StoryConfig />;
      actions = null;
      break;

    case 'Districts':
      content = <Districts />;
      actions = null;
      break;

    case 'Schools':
      content = <Schools onOptionClick={handleOptionClick} />;
      actions = null;
      break;

    case 'Countries':
      content = <Countries />;
      actions = null;
      break;

    case 'Classrooms':
      content = (
        <Classrooms
          onOptionClick={handleOptionClick}
          initialTeacherEmail={initialSearchText}
          initialClassroomId={initialClassroomId}
        />
      );
      actions = null;
      break;
  }
  return (
    <div className="flex flex-col sm:flex-row bg-storytan min-h-screen">
      <Sidebar
        onOptionClick={handleOptionClick}
        selectedOption={selectedOption}
      />
      <main className="flex-1 flex flex-col bg-storytan min-w-0">
        <div className="flex flex-col min-w-0">
          <div className="p-4">
            <div className="flex align-center justify-between ml-0 md:ml-4 mr-0 md:mr-4 mt-0 md:mt-5">
              <div className=" flex items-center align-end mb-4">
                <Heading title={adminPageLabel(selectedOption)} />
                <div className="mt-1">
                </div>
              </div>
              {actions && <div className="flex items-center align-end">{actions}</div>}
            </div>
            {cameFrom && (
              <button
                onClick={() => { setCameFrom(undefined); handleOptionClick(cameFrom); }}
                className="ml-0 md:ml-4 mb-3 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <span className="text-lg leading-none">&larr;</span> Back to {adminPageLabel(cameFrom)}
              </button>
            )}
            {content}
          </div>
        </div>
      </main>
    </div>
  );
};
export default Dashboard;
